import axios from "axios";
import React, { forwardRef, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";
import "./css/login-page.css";
import { getAssetUrl } from "../../utils/assetUrl";
import { getApiBase } from "../../utils/apiBase";

const registerSchema = yup.object().shape({
  username: yup.string().required("Tên tài khoản bắt buộc"),
  password: yup.string().min(6, "Mật khẩu ít nhất 6 ký tự").required(),
  password_confirmation: yup
    .string()
    .oneOf([yup.ref("password")], "Mật khẩu không khớp")
    .required("Vui lòng nhập lại mật khẩu"),
  phone_number: yup
    .string()
    .matches(/^\d{10}$/, "Số điện thoại phải có 10 chữ số")
    .required("Số điện thoại bắt buộc"),
});

type RegisterFormValues = yup.InferType<typeof registerSchema>;

type AuthFieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  iconSrc?: string;
  iconAlt?: string;
  showToggle?: boolean;
  visible?: boolean;
  onToggle?: () => void;
  variant?: "default" | "referral" | "phone";
};

const AuthField = forwardRef<HTMLInputElement, AuthFieldProps>(function AuthField(
  {
    iconSrc,
    iconAlt = "",
    placeholder,
    type = "text",
    showToggle,
    visible,
    onToggle,
    variant = "default",
    className,
    ...inputProps
  },
  ref
) {
  const fieldClass =
    variant === "referral" ? " login-field--referral" : variant === "phone" ? " login-field--phone" : "";

  return (
    <div className={`login-field${fieldClass}${className ? ` ${className}` : ""}`}>
      {iconSrc ? (
        <img className="login-field__icon" src={iconSrc} alt={iconAlt} width={37} height={34} />
      ) : null}
      <input
        ref={ref}
        className="login-field__input"
        type={showToggle ? (visible ? "text" : "password") : type}
        placeholder={placeholder}
        {...inputProps}
      />
      {showToggle ? (
        <button
          type="button"
          className="login-field__eye"
          onClick={onToggle}
          aria-label={visible ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
        >
          <img src={getAssetUrl("/assets/thn/icon-eye.svg")} alt="" width={28} height={17} />
        </button>
      ) : null}
    </div>
  );
});

const Login: React.FC = () => {
  const Cookies = require("js-cookie");

  const [authView, setAuthView] = useState<"login" | "register">("login");
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegPassword2, setShowRegPassword2] = useState(false);
  const [referralCode, setReferralCode] = useState("");

  const {
    register: registerField,
    handleSubmit: handleRegisterSubmit,
    reset: resetRegisterForm,
    formState: { errors: registerErrors },
  } = useForm<RegisterFormValues>({
    resolver: yupResolver(registerSchema),
    defaultValues: {
      username: "",
      phone_number: "",
      password: "",
      password_confirmation: "",
    },
  });

  useEffect(() => {
    document.documentElement.classList.add("login-scroll-lock");
    document.body.classList.add("login-scroll-lock");
    return () => {
      document.documentElement.classList.remove("login-scroll-lock");
      document.body.classList.remove("login-scroll-lock");
    };
  }, []);

  const handleLogin = async () => {
    if (userName.trim() === "" || password.trim() === "") {
      Swal.fire({
        icon: "error",
        title: "Lỗi đăng nhập",
        text: "Vui lòng điền đầy đủ thông tin",
        customClass: {
          popup: "custom-swal",
          title: "custom-title",
          htmlContainer: "custom-text",
        },
      });
      return;
    }

    try {
      Swal.fire({
        title: "Đang xử lý...",
        html: "<p class='swal-loading-subtext'>Vui lòng chờ trong giây lát</p><div class='swal-loading-dots'><span class='swal-dot swal-dot-cyan'></span><span class='swal-dot swal-dot-orange'></span></div>",
        customClass: {
          popup: "custom-swal swal-loading-modal",
          title: "custom-title",
          htmlContainer: "custom-text",
        },
        allowOutsideClick: false,
        showConfirmButton: false,
      });

      await new Promise((resolve) => setTimeout(resolve, 1000));
      await axios
        .post(`${getApiBase()}/auth/login`, {
          username: userName,
          password,
        })
        .then(async (data) => {
          localStorage.setItem(
            "user_info",
            JSON.stringify({
              userName: data.data.user.username,
              coins: data.data.user.coins,
              role: data.data.user.role,
              id: data.data.user._id,
            })
          );

          Cookies.set("access_token", data.data.access_token, {
            expires: 1 / 24,
            secure: false,
            sameSite: "Lax",
          });

          await new Promise((resolve) => setTimeout(resolve, 100));

          if (
            (data.status === 200 || data.status === 201) &&
            (data.data.user.role === "SUPERADMIN" || data.data.user.role === "ADMIN")
          ) {
            Swal.fire({
              icon: "success",
              title: "Đăng nhập thành công",
              customClass: {
                popup: "custom-swal",
                title: "custom-title",
                htmlContainer: "custom-text",
              },
            });
            setTimeout(() => {
              window.location.href = "/admin";
            }, 500);
          } else {
            Swal.fire({
              icon: "success",
              title: "Đăng nhập thành công",
              text: "Vui lòng đợi ít phút. ",
              customClass: {
                popup: "custom-swal",
                title: "custom-title",
                htmlContainer: "custom-text",
              },
            });
            setTimeout(() => {
              window.location.href = "/";
            }, 500);
          }
        })
        .catch((err) => {
          const status = err.response?.status;
          const message =
            status === 401
              ? "Tài khoản/mật khẩu không chính xác!"
              : err.response?.data?.message ||
                err.message ||
                "Không kết nối được máy chủ. Thử lại sau.";
          Swal.fire({
            icon: "error",
            title: "Lỗi đăng nhập",
            text: Array.isArray(message) ? message.join("\n") : String(message),
            customClass: {
              popup: "custom-swal",
              title: "custom-title",
              htmlContainer: "custom-text",
            },
          });
        });
    } catch (error) {
      return error;
    }
  };

  const onRegister = async (data: RegisterFormValues) => {
    try {
      await axios
        .post(`${getApiBase()}/auth/register`, {
          username: data.username,
          phone: data.phone_number,
          password: String(data.password),
        })
        .then((res) => {
          if (res.status === 201) {
            Swal.fire({
              icon: "success",
              title: "Đăng ký thành công",
              text: "Vui lòng đợi ít phút. ",
              customClass: {
                popup: "custom-swal",
                title: "custom-title",
                htmlContainer: "custom-text",
              },
            });
            resetRegisterForm();
            setAuthView("login");
          }
        })
        .catch((err) => {
          Swal.fire({
            icon: "error",
            title: "Lỗi đăng ký",
            text: `${err.response?.data?.message || "Có lỗi xảy ra, vui lòng thử lại!"}`,
            customClass: {
              popup: "custom-swal",
              title: "custom-title",
              htmlContainer: "custom-text",
            },
          });
        });
    } catch (error) {
      return error;
    }
  };

  const goRegister = () => {
    setAuthView("register");
    setReferralCode("");
    resetRegisterForm();
  };

  const goLogin = () => {
    setAuthView("login");
    setReferralCode("");
    resetRegisterForm();
  };

  return (
    <div className="login-page">
      <h1 className="login-page__brand">_Toolhacknhacai.com_</h1>

      <div
        className={`login-page__modal${authView === "register" ? " login-page__modal--register" : ""}`}
        role="dialog"
        aria-label={authView === "login" ? "Đăng nhập" : "Đăng ký"}
      >
        <h2
          className={`login-page__form-title${
            authView === "register" ? " login-page__form-title--register" : ""
          }`}
        >
          {authView === "login" ? "ĐĂNG NHẬP" : "ĐĂNG KÝ"}
        </h2>

        {authView === "login" ? (
          <form
            className="login-page__form"
            id="loginform"
            onSubmit={(e) => {
              e.preventDefault();
              handleLogin();
            }}
          >
            <AuthField
              iconSrc={getAssetUrl("/assets/thn/icon-user.svg")}
              placeholder="Nhập tên đăng nhập"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              autoComplete="username"
              maxLength={16}
            />
            <AuthField
              iconSrc={getAssetUrl("/assets/thn/icon-lock.svg")}
              placeholder="Nhập mật khẩu"
              showToggle
              visible={showPassword}
              onToggle={() => setShowPassword((v) => !v)}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              maxLength={16}
            />
            <button type="submit" className="login-page__submit">
              {`> ĐĂNG NHẬP <`}
            </button>
          </form>
        ) : (
          <form
            className="login-page__form"
            id="registerform"
            onSubmit={handleRegisterSubmit(onRegister)}
            noValidate
          >
            <AuthField
              iconSrc={getAssetUrl("/assets/thn/icon-user.svg")}
              placeholder="Nhập tên đăng nhập"
              autoComplete="username"
              {...registerField("username")}
            />
            {registerErrors.username && (
              <p className="login-page__error">{registerErrors.username.message}</p>
            )}
            <AuthField
              iconSrc={getAssetUrl("/assets/thn/icon-lock.svg")}
              placeholder="Nhập mật khẩu"
              showToggle
              visible={showRegPassword}
              onToggle={() => setShowRegPassword((v) => !v)}
              autoComplete="new-password"
              {...registerField("password")}
            />
            {registerErrors.password && (
              <p className="login-page__error">{registerErrors.password.message}</p>
            )}
            <AuthField
              iconSrc={getAssetUrl("/assets/thn/icon-lock.svg")}
              placeholder="Nhập lại mật khẩu"
              showToggle
              visible={showRegPassword2}
              onToggle={() => setShowRegPassword2((v) => !v)}
              autoComplete="new-password"
              {...registerField("password_confirmation")}
            />
            {registerErrors.password_confirmation && (
              <p className="login-page__error">
                {registerErrors.password_confirmation.message}
              </p>
            )}
            <AuthField
              iconSrc={getAssetUrl("/assets/thn/icon-phone.svg")}
              placeholder="+84..."
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              maxLength={10}
              {...registerField("phone_number")}
            />
            {registerErrors.phone_number && (
              <p className="login-page__error">{registerErrors.phone_number.message}</p>
            )}
            <AuthField
              variant="referral"
              iconSrc={getAssetUrl("/assets/thn/icon-referral.svg")}
              placeholder="Nhập mã giới thiệu (nếu có)"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
            />
            <button type="submit" className="login-page__submit">
              {`> ĐĂNG KÝ <`}
            </button>
          </form>
        )}

        {authView === "login" ? (
          <p className="login-page__footer">
            Chưa có tài khoản?{" "}
            <span
              className="login-page__link"
              role="button"
              tabIndex={0}
              onClick={goRegister}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  goRegister();
                }
              }}
            >
              LINK ĐĂNG KÝ
            </span>
          </p>
        ) : (
          <p className="login-page__footer">
            Đã có tài khoản?{" "}
            <span
              className="login-page__link"
              role="button"
              tabIndex={0}
              onClick={goLogin}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  goLogin();
                }
              }}
            >
              LINK ĐĂNG NHẬP
            </span>
          </p>
        )}
      </div>
    </div>
  );
};

export default Login;
